import { FormGroup, Icon, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { MultiSelect } from "@blueprintjs/select";
import React from "react";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  BaseEnumFieldProps,
  EnumMultiSelectFieldSingleValue,
  EnumMultiSelectFieldValue,
  FieldLabeledProps,
  Option,
} from "../../types/fieldTypes";
import { useEnumMultiSelectField } from "./useEnumMultiSelectField";

/**Interface for EnumMultiSelectField*/
export interface EnumMultiSelectFieldProps
  extends BaseEnumFieldProps<
    EnumMultiSelectFieldValue,
    EnumMultiSelectFieldSingleValue
  > {
  includeSelectAll?: boolean;
  placeholder?: string;
  labelProps?: FieldLabeledProps;
}

/**Enum Multi Select is used primarily with enum values from the API, but can be passed collection of arrays.
 * Allows for multiple selections.
 */
const EnumMultiSelectField: React.FC<EnumMultiSelectFieldProps> = (props) => {
  const { localValue, setLocalValue, localOptionValues, selectAllOption } =
    useEnumMultiSelectField(props);

  return (
    <FormGroup
      className={`${
        props.isRequired && !!localValue && (localValue || []).length === 0
          ? "has-required-background"
          : "base-field"
      }`}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div
        className={`${
          (localValue || []).length === 0
            ? "multi-select-container__empty"
            : "multi-select-container"
        }`}
      >
        <MultiSelect<Option<EnumMultiSelectFieldSingleValue>>
          className="multi-select-input"
          placeholder={
            props.placeholder ? props.placeholder : "Please select all that apply..."
          }
          disabled={props.disabled}
          selectedItems={
            localValue
              .map(
                (value) =>
                  (localOptionValues || []).find(
                    (optionValue) => optionValue.value === value
                  ) || []
              )
              .filter(
                (value) => !!value
              ) as Option<EnumMultiSelectFieldSingleValue>[]
          }
          popoverProps={{
            position: "auto",
            hasBackdrop: true,
            minimal: false,
            canEscapeKeyClose: true,
          }}
          items={
            props.includeSelectAll ? localOptionValues : props.optionValues
          }
          itemRenderer={(item, itemProps) => {
            return (
              <MenuItem
                className="select__menu-item"
                key={itemProps.index}
                text={item.label}
                active={localValue.some(
                  (value) => String(value) === String(item.value)
                )}
                onClick={itemProps.handleClick}
              />
            );
          }}
          onClear={() => {
            const newValue: any[] = [];
            setLocalValue(newValue);
            props.onSubmit && props.onSubmit(newValue);
          }}
          noResults={"No Results"}
          resetOnSelect={true}
          onRemove={(item) => {
            let newValue: any[] = [];
            if (item.value === selectAllOption.value) {
              newValue = [];
            } else {
              newValue = (localValue || []).filter(
                (value) =>
                  value !== item.value && value !== selectAllOption.value
              );
            }
            setLocalValue(newValue);
            props.onSubmit && props.onSubmit(newValue);
          }}
          onItemSelect={(item) => {
            let newValue: any[] = [];

            if (
              localValue.some((value) => String(value) === String(item.value))
            ) {
              if (item.value === selectAllOption.value) {
                newValue = [];
                setLocalValue(newValue);
              } else {
                newValue = (localValue || []).filter(
                  (value) =>
                    value !== item.value && value !== selectAllOption.value
                );
                setLocalValue(newValue);
              }
            } else {
              if (item.value === selectAllOption.value) {
                newValue = props.optionValues.map(
                  (optionValue) => optionValue.value
                );
                setLocalValue([selectAllOption.value, ...newValue]);
              } else {
                newValue = [...localValue, item.value];
                setLocalValue(newValue);
              }
            }
            props.onSubmit && props.onSubmit(newValue);
          }}
          tagRenderer={(item) => item.label}
          itemListPredicate={(query, items) => {
            query = query.toLowerCase();

            return (items || []).filter((optionValue) =>
              optionValue.label.toLowerCase().includes(query)
            );
          }}
        />
        <Icon className="select-right-icon" icon={IconNames.CaretDown} />
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default EnumMultiSelectField;
